import { useState, useMemo } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  HelpCircle,
  Download,
  Shield,
  Settings,
  CheckCircle2,
  ExternalLink,
  Server,
  FileKey,
  Globe,
  Building2,
  FileText,
  Package,
  CreditCard,
  PlayCircle,
  AlertTriangle,
  DollarSign,
  Wifi,
  Clock,
  Printer,
  Monitor,
  Terminal,
  Copy,
  Info,
} from "lucide-react";
import { toast } from "sonner";
import { useRestaurantContext } from "@/contexts/RestaurantContext";
import { useFiscalConfig } from "@/hooks/useFiscalConfig";

const copyToClipboard = (text: string, label: string) => {
  navigator.clipboard.writeText(text);
  toast.success(`${label} copiado!`);
};

export function FiscalSetupGuide() {
  const [open, setOpen] = useState(false);
  const { activeRestaurant } = useRestaurantContext();
  const { config } = useFiscalConfig();

  const restaurantSlug = useMemo(() => {
    if (!activeRestaurant?.name) return "meu-restaurante";
    return activeRestaurant.name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  }, [activeRestaurant?.name]);

  const remotePort = config?.acbr_port ?? 3434;
  const acbrHost = config?.acbr_host ?? "212.85.17.133";

  const frpcToml = `serverAddr = "${acbrHost}"
serverPort = 7000
auth.method = "token"
auth.token = "SuaSenhaSeguraAqui123"

[[proxies]]
name = "acbr-${restaurantSlug}"
type = "tcp"
localIP = "127.0.0.1"
localPort = 3434
remotePort = ${remotePort}`;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <HelpCircle className="h-4 w-4" />
          Guia de Instalação
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <FileText className="h-5 w-5" />
            Configuração de Emissão NFC-e
          </DialogTitle>
          <DialogDescription>
            Siga o passo a passo completo para configurar a emissão de notas fiscais
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Requisitos */}
          <Card className="border-l-4 border-l-primary">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge variant="default" className="h-7 w-7 rounded-full p-0 flex items-center justify-center">
                  1
                </Badge>
                <CheckCircle2 className="h-5 w-5 text-primary" />
                Requisitos Necessários
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Antes de começar, certifique-se de ter os seguintes itens:
              </p>
              
              <div className="grid gap-2 text-sm">
                <div className="flex items-start gap-2 p-2 bg-muted rounded-lg">
                  <FileKey className="h-4 w-4 mt-0.5 text-primary" />
                  <div>
                    <span className="font-medium">Certificado Digital A1</span>
                    <p className="text-muted-foreground text-xs">Arquivo .pfx válido e senha</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 bg-muted rounded-lg">
                  <Server className="h-4 w-4 mt-0.5 text-primary" />
                  <div>
                    <span className="font-medium">Computador Windows</span>
                    <p className="text-muted-foreground text-xs">Windows 7 ou superior, sempre ligado</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 bg-muted rounded-lg">
                  <Globe className="h-4 w-4 mt-0.5 text-primary" />
                  <div>
                    <span className="font-medium">Acesso à Internet</span>
                    <p className="text-muted-foreground text-xs">Para comunicação com a SEFAZ</p>
                  </div>
                </div>
                <div className="flex items-start gap-2 p-2 bg-muted rounded-lg">
                  <Shield className="h-4 w-4 mt-0.5 text-primary" />
                  <div>
                    <span className="font-medium">CSC (Código de Segurança)</span>
                    <p className="text-muted-foreground text-xs">Obtido no portal da SEFAZ do seu estado</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Instalar ACBrMonitor */}
          <Card className="border-l-4 border-l-orange-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-orange-500">
                  2
                </Badge>
                <Download className="h-5 w-5 text-orange-500" />
                Instalar o ACBrMonitorPlus
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                O ACBrMonitorPlus é o software que faz a comunicação com a SEFAZ para emissão das notas.
              </p>
              
              <Button
                variant="default"
                className="gap-2"
                onClick={() => window.open("https://projetoacbr.com.br/acbrmonitorplus/", "_blank")}
              >
                <ExternalLink className="h-4 w-4" />
                Baixar ACBrMonitorPlus
              </Button>

              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>Execute o instalador e siga as instruções padrão</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>Na primeira execução, cadastre uma licença (versão DEMO para testes)</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-muted-foreground" />
                  <span>Configure para iniciar automaticamente com o Windows</span>
                </div>
              </div>

              <Alert>
                <DollarSign className="h-4 w-4" />
                <AlertDescription>
                  A versão DEMO funciona para testes em homologação. Para produção, é necessário adquirir 
                  a licença PRO (pagamento único de aproximadamente R$ 500).
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Configurar Certificado */}
          <Card className="border-l-4 border-l-blue-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-blue-500">
                  3
                </Badge>
                <FileKey className="h-5 w-5 text-blue-500" />
                Configurar Certificado Digital
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Configure o certificado digital A1 no ACBrMonitorPlus:
              </p>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">1</span>
                  <span>Abra o ACBrMonitorPlus</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">2</span>
                  <span>Vá em <strong>Configurações → NFe/NFCe → Certificado</strong></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">3</span>
                  <span>Clique em <strong>"Selecionar Certificado"</strong> e escolha o arquivo .pfx</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">4</span>
                  <span>Informe a senha do certificado</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">5</span>
                  <span>Clique em <strong>"Testar"</strong> para validar</span>
                </div>
              </div>

              <Alert className="bg-blue-500/10 border-blue-500/20">
                <Shield className="h-4 w-4 text-blue-500" />
                <AlertDescription className="text-blue-700 dark:text-blue-300">
                  O certificado A1 tem validade de 1 ano. Anote a data de vencimento e renove antes de expirar.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Configurar TCP */}
          <Card className="border-l-4 border-l-purple-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-purple-500">
                  4
                </Badge>
                <Server className="h-5 w-5 text-purple-500" />
                Configurar Servidor TCP
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Ative o servidor TCP para que o sistema possa se comunicar com o ACBrMonitor:
              </p>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">1</span>
                  <span>Vá em <strong>Configurações → Monitor</strong></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">2</span>
                  <span>Marque <strong>"Ativar Servidor TCP/IP"</strong></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">3</span>
                  <span>Porta: <code className="bg-muted px-1 rounded">3434</code> (padrão)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">4</span>
                  <span>Marque <strong>"Permitir Comandos Remotos"</strong></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">5</span>
                  <span>Clique em <strong>"Salvar"</strong> e reinicie o ACBrMonitor</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Configurar NFC-e */}
          <Card className="border-l-4 border-l-emerald-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-emerald-500">
                  5
                </Badge>
                <FileText className="h-5 w-5 text-emerald-500" />
                Configurar NFC-e no ACBrMonitor
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Configure os parâmetros da NFC-e:
              </p>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">1</span>
                  <span>Vá em <strong>Configurações → NFe/NFCe</strong></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">2</span>
                  <span>Modelo: <strong>65 (NFC-e)</strong></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">3</span>
                  <span>Ambiente: <strong>2 (Homologação)</strong> para testes</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">4</span>
                  <span>Versão QR-Code: <strong>2</strong></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">5</span>
                  <span>Informe o CSC ID e Token (obtidos na SEFAZ)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">6</span>
                  <span>Série: <strong>1</strong></span>
                </div>
              </div>

              <Alert variant="destructive" className="bg-destructive/10 border-destructive/20">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  <strong>Importante:</strong> Sempre comece em ambiente de homologação (testes). 
                  Só mude para produção após validar todo o processo.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* FRP - Túnel */}
          <Card className="border-l-4 border-l-pink-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-pink-500">
                  6
                </Badge>
                <Globe className="h-5 w-5 text-pink-500" />
                Configurar Túnel FRP (Cliente Windows)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                O FRP (Fast Reverse Proxy) cria um túnel seguro para que o sistema na nuvem acesse o ACBrMonitor local. 
                Diferente do ngrok, o FRP usa IP fixo e inicia automaticamente como serviço.
              </p>

              <Alert className="bg-pink-500/10 border-pink-500/20">
                <Server className="h-4 w-4 text-pink-500" />
                <AlertDescription className="text-pink-700 dark:text-pink-300">
                  <strong>Arquitetura Multi-Restaurante:</strong> O servidor FRP já está configurado na VPS Altus Food. 
                  Cada restaurante recebe uma <strong>porta remota única</strong> (ex: 3434, 3435, 3436...) para evitar conflitos.
                  Localmente, todos usam a porta 3434 do ACBrMonitor.
                </AlertDescription>
              </Alert>

              <div className="flex flex-wrap gap-2">
                <Button
                  variant="default"
                  className="gap-2"
                  onClick={() => window.open("https://github.com/fatedier/frp/releases", "_blank")}
                >
                  <ExternalLink className="h-4 w-4" />
                  Baixar FRP
                </Button>
                <Button
                  variant="outline"
                  className="gap-2"
                  onClick={() => window.open("https://nssm.cc/download", "_blank")}
                >
                  <ExternalLink className="h-4 w-4" />
                  Baixar NSSM
                </Button>
              </div>

              <div className="space-y-3 text-sm">
                <p className="font-medium">Instalação no Windows:</p>
                
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">1</span>
                  <span>Baixe <code className="bg-muted px-1 rounded">frp_*_windows_amd64.zip</code> do GitHub</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">2</span>
                  <span>Extraia para <code className="bg-muted px-1 rounded">C:\frp</code></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">3</span>
                  <span>Crie o arquivo <code className="bg-muted px-1 rounded">frpc.toml</code> com o conteúdo abaixo:</span>
                </div>
              </div>

              <div className="relative">
                <pre className="bg-muted p-3 rounded-lg text-xs overflow-x-auto font-mono">
{frpcToml}
                </pre>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6"
                  onClick={() => copyToClipboard(frpcToml, "Configuração frpc.toml")}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>

              <Alert className="bg-emerald-500/10 border-emerald-500/20">
                <Info className="h-4 w-4 text-emerald-600" />
                <AlertDescription className="text-emerald-700 dark:text-emerald-300">
                  Valores preenchidos automaticamente: restaurante <strong>{activeRestaurant?.name || "—"}</strong>, 
                  porta remota <strong>{remotePort}</strong>, host <strong>{acbrHost}</strong>.
                </AlertDescription>
              </Alert>

              <div className="space-y-3 text-sm">
                <p className="font-medium flex items-center gap-2">
                  <Terminal className="h-4 w-4" />
                  Teste manual (antes de instalar como serviço):
                </p>
                <div className="relative">
                  <pre className="bg-muted p-3 rounded-lg text-xs overflow-x-auto font-mono">
{`cd C:\\frp
frpc.exe -c frpc.toml`}
                  </pre>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 h-6 w-6"
                    onClick={() => copyToClipboard("cd C:\\frp\nfrpc.exe -c frpc.toml", "Comando")}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <p className="text-muted-foreground">Se aparecer <code className="bg-muted px-1 rounded">start proxy success</code>, está funcionando!</p>
              </div>
            </CardContent>
          </Card>

          {/* NSSM - Serviço Windows */}
          <Card className="border-l-4 border-l-violet-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-violet-500">
                  6B
                </Badge>
                <Monitor className="h-5 w-5 text-violet-500" />
                Instalar FRP como Serviço Windows (NSSM)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                O NSSM permite que o túnel FRP inicie automaticamente com o Windows e reinicie se cair.
              </p>

              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">1</span>
                  <span>Baixe o NSSM e extraia <code className="bg-muted px-1 rounded">nssm.exe</code> (pasta win64) para <code className="bg-muted px-1 rounded">C:\frp</code></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">2</span>
                  <span>Crie pasta de logs: <code className="bg-muted px-1 rounded">mkdir C:\frp\logs</code></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">3</span>
                  <span>Abra o <strong>Prompt de Comando como Administrador</strong> e execute:</span>
                </div>
              </div>

              <div className="relative">
                <pre className="bg-muted p-3 rounded-lg text-xs overflow-x-auto font-mono">
{`cd C:\\frp
nssm install frpc`}
                </pre>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6"
                  onClick={() => copyToClipboard("cd C:\\frp\nnssm install frpc", "Comando")}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>

              <div className="space-y-2 text-sm">
                <p className="font-medium">Na janela do NSSM, configure:</p>
                <div className="grid gap-2">
                  <div className="p-2 bg-muted rounded-lg">
                    <span className="font-medium">Path:</span>
                    <code className="ml-2 text-xs">C:\frp\frpc.exe</code>
                  </div>
                  <div className="p-2 bg-muted rounded-lg">
                    <span className="font-medium">Startup directory:</span>
                    <code className="ml-2 text-xs">C:\frp</code>
                  </div>
                  <div className="p-2 bg-muted rounded-lg">
                    <span className="font-medium">Arguments:</span>
                    <code className="ml-2 text-xs">-c frpc.toml</code>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <p className="font-medium">Na aba "Exit" (para reinício automático):</p>
                <div className="grid gap-2">
                  <div className="p-2 bg-muted rounded-lg">
                    <span className="font-medium">Restart:</span>
                    <span className="ml-2 text-muted-foreground">Restart application</span>
                  </div>
                  <div className="p-2 bg-muted rounded-lg">
                    <span className="font-medium">Delay restart by:</span>
                    <code className="ml-2 text-xs">5000</code>
                    <span className="text-muted-foreground ml-1">ms</span>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <p className="font-medium">Na aba "I/O" (logs):</p>
                <div className="grid gap-2">
                  <div className="p-2 bg-muted rounded-lg">
                    <span className="font-medium">Output (stdout):</span>
                    <code className="ml-2 text-xs">C:\frp\logs\frpc-out.log</code>
                  </div>
                  <div className="p-2 bg-muted rounded-lg">
                    <span className="font-medium">Error (stderr):</span>
                    <code className="ml-2 text-xs">C:\frp\logs\frpc-err.log</code>
                  </div>
                </div>
              </div>

              <div className="space-y-2 text-sm">
                <p className="font-medium">Clique em "Install service" e depois inicie:</p>
                <div className="relative">
                  <pre className="bg-muted p-3 rounded-lg text-xs overflow-x-auto font-mono">
{`nssm start frpc
nssm status frpc`}
                  </pre>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="absolute top-2 right-2 h-6 w-6"
                    onClick={() => copyToClipboard("nssm start frpc\nnssm status frpc", "Comandos")}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
                <p className="text-muted-foreground">Deve retornar: <code className="bg-muted px-1 rounded">SERVICE_RUNNING</code></p>
              </div>

              <Alert className="bg-violet-500/10 border-violet-500/20">
                <CheckCircle2 className="h-4 w-4 text-violet-500" />
                <AlertDescription className="text-violet-700 dark:text-violet-300">
                  <strong>Pronto!</strong> O túnel agora inicia automaticamente com o Windows. 
                  Reinicie o computador para testar.
                </AlertDescription>
              </Alert>

              <div className="space-y-2 text-sm">
                <p className="font-medium">Comandos úteis para gerenciamento:</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="p-2 bg-muted rounded-lg text-xs">
                    <code>nssm restart frpc</code>
                    <p className="text-muted-foreground">Reiniciar</p>
                  </div>
                  <div className="p-2 bg-muted rounded-lg text-xs">
                    <code>nssm stop frpc</code>
                    <p className="text-muted-foreground">Parar</p>
                  </div>
                  <div className="p-2 bg-muted rounded-lg text-xs">
                    <code>nssm edit frpc</code>
                    <p className="text-muted-foreground">Editar config</p>
                  </div>
                  <div className="p-2 bg-muted rounded-lg text-xs">
                    <code>nssm remove frpc confirm</code>
                    <p className="text-muted-foreground">Remover serviço</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Separator />

          {/* Configuração no Sistema */}
          <div className="text-center py-2">
            <h3 className="text-lg font-semibold">Configuração no Sistema</h3>
            <p className="text-sm text-muted-foreground">Agora configure o sistema para usar o ACBrMonitor</p>
          </div>

          {/* Dados do Emitente */}
          <Card className="border-l-4 border-l-cyan-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-cyan-500">
                  7
                </Badge>
                <Building2 className="h-5 w-5 text-cyan-500" />
                Dados do Emitente
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                No sistema, vá em <strong>Fiscal → Emitente</strong> e preencha:
              </p>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 bg-muted rounded-lg">
                  <span className="font-medium">CNPJ</span>
                  <p className="text-muted-foreground text-xs">Apenas números</p>
                </div>
                <div className="p-2 bg-muted rounded-lg">
                  <span className="font-medium">Inscrição Estadual</span>
                  <p className="text-muted-foreground text-xs">Apenas números</p>
                </div>
                <div className="p-2 bg-muted rounded-lg">
                  <span className="font-medium">Razão Social</span>
                  <p className="text-muted-foreground text-xs">Nome legal da empresa</p>
                </div>
                <div className="p-2 bg-muted rounded-lg">
                  <span className="font-medium">Nome Fantasia</span>
                  <p className="text-muted-foreground text-xs">Nome comercial</p>
                </div>
                <div className="p-2 bg-muted rounded-lg">
                  <span className="font-medium">CEP e Endereço</span>
                  <p className="text-muted-foreground text-xs">Completo com número</p>
                </div>
                <div className="p-2 bg-muted rounded-lg">
                  <span className="font-medium">Código IBGE</span>
                  <p className="text-muted-foreground text-xs">Preenchido automaticamente</p>
                </div>
              </div>

              <Alert className="bg-cyan-500/10 border-cyan-500/20">
                <Building2 className="h-4 w-4 text-cyan-500" />
                <AlertDescription className="text-cyan-700 dark:text-cyan-300">
                  <strong>CRT (Regime Tributário):</strong> Confirme com seu contador se é 
                  Simples Nacional (1) ou Regime Normal (3). Deve ser igual ao configurado no ACBrMonitor.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Configurar Integração */}
          <Card className="border-l-4 border-l-amber-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-amber-500">
                  8
                </Badge>
                <Settings className="h-5 w-5 text-amber-500" />
                Configurar Integração
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Em <strong>Fiscal → Configurações</strong>, configure:
              </p>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-amber-500" />
                  <span>Integrador: <strong>ACBrMonitorPlus</strong></span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-amber-500" />
                  <span>Host: <code className="bg-muted px-1 rounded">212.85.17.133</code> (IP fixo da VPS)</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-amber-500" />
                  <span>Porta: Sua porta designada (ex: <code className="bg-muted px-1 rounded">3434</code>, <code className="bg-muted px-1 rounded">3435</code>...)</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-amber-500" />
                  <span>Clique em <strong>"Testar Conexão"</strong> para validar</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-amber-500" />
                  <span>Informe CSC ID e Token</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-amber-500" />
                  <span>Ambiente: <strong>Homologação</strong> para testes</span>
                </div>
              </div>

              <Alert className="bg-amber-500/10 border-amber-500/20">
                <Wifi className="h-4 w-4 text-amber-500" />
                <AlertDescription className="text-amber-700 dark:text-amber-300">
                  <strong>Diferente do ngrok:</strong> O IP e porta são fixos e não mudam ao reiniciar. 
                  Configure uma vez e funciona para sempre!
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Regras Tributárias */}
          <Card className="border-l-4 border-l-indigo-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-indigo-500">
                  9
                </Badge>
                <FileText className="h-5 w-5 text-indigo-500" />
                Criar Regras Tributárias
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Em <strong>Fiscal → Regras Tributárias</strong>, crie regras para cada tipo de produto:
              </p>
              
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2">Tipo</th>
                      <th className="text-left py-2 px-2">NCM</th>
                      <th className="text-left py-2 px-2">CFOP</th>
                    </tr>
                  </thead>
                  <tbody className="text-muted-foreground">
                    <tr className="border-b">
                      <td className="py-2 px-2">Alimentos Prontos</td>
                      <td className="py-2 px-2 font-mono">21069090</td>
                      <td className="py-2 px-2 font-mono">5102</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-2">Bebidas Não Alcoólicas</td>
                      <td className="py-2 px-2 font-mono">22029900</td>
                      <td className="py-2 px-2 font-mono">5102</td>
                    </tr>
                    <tr className="border-b">
                      <td className="py-2 px-2">Bebidas Alcoólicas</td>
                      <td className="py-2 px-2 font-mono">22030000</td>
                      <td className="py-2 px-2 font-mono">5102</td>
                    </tr>
                    <tr>
                      <td className="py-2 px-2">Revenda (geral)</td>
                      <td className="py-2 px-2 font-mono">Variável</td>
                      <td className="py-2 px-2 font-mono">5102</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <Alert className="bg-indigo-500/10 border-indigo-500/20">
                <FileText className="h-4 w-4 text-indigo-500" />
                <AlertDescription className="text-indigo-700 dark:text-indigo-300">
                  <strong>Consulte seu contador</strong> para definir CSOSN, CST, PIS e COFINS 
                  corretos para seu regime tributário.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          {/* Vincular Produtos */}
          <Card className="border-l-4 border-l-rose-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-rose-500">
                  10
                </Badge>
                <Package className="h-5 w-5 text-rose-500" />
                Vincular Produtos às Regras
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Em <strong>Fiscal → Produtos</strong>, vincule cada produto a uma regra tributária:
              </p>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-rose-500" />
                  <span>Use <strong>"Atribuir Regra em Lote"</strong> para vincular produtos de uma categoria inteira</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-rose-500" />
                  <span>Ou edite individualmente para NCMs específicos</span>
                </div>
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 text-rose-500" />
                  <span>Produtos sem regra não podem ser emitidos em NFC-e</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Meios de Pagamento */}
          <Card className="border-l-4 border-l-teal-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-teal-500">
                  11
                </Badge>
                <CreditCard className="h-5 w-5 text-teal-500" />
                Configurar Meios de Pagamento
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Em <strong>Configurações → Formas de Pagamento</strong>, defina o código fiscal para cada forma:
              </p>
              
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="p-2 bg-muted rounded-lg flex justify-between">
                  <span>Dinheiro</span>
                  <code className="font-mono">01</code>
                </div>
                <div className="p-2 bg-muted rounded-lg flex justify-between">
                  <span>Cartão Crédito</span>
                  <code className="font-mono">03</code>
                </div>
                <div className="p-2 bg-muted rounded-lg flex justify-between">
                  <span>Cartão Débito</span>
                  <code className="font-mono">04</code>
                </div>
                <div className="p-2 bg-muted rounded-lg flex justify-between">
                  <span>PIX</span>
                  <code className="font-mono">17</code>
                </div>
                <div className="p-2 bg-muted rounded-lg flex justify-between">
                  <span>Vale Alimentação</span>
                  <code className="font-mono">10</code>
                </div>
                <div className="p-2 bg-muted rounded-lg flex justify-between">
                  <span>Vale Refeição</span>
                  <code className="font-mono">11</code>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Testar Emissão */}
          <Card className="border-l-4 border-l-green-500">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-3 text-lg">
                <Badge className="h-7 w-7 rounded-full p-0 flex items-center justify-center bg-green-500">
                  12
                </Badge>
                <PlayCircle className="h-5 w-5 text-green-500" />
                Testar Emissão
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Faça um teste completo de emissão em homologação:
              </p>
              
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">1</span>
                  <span>Faça uma venda simples no PDV</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">2</span>
                  <span>No checkout, clique em <strong>"Finalizar e Emitir NFC-e"</strong></span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">3</span>
                  <span>Aguarde a resposta da SEFAZ</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-mono bg-muted px-2 py-0.5 rounded text-xs">4</span>
                  <span>Verifique o status em <strong>Fiscal → NFC-e Emitidas</strong></span>
                </div>
              </div>

              <Alert className="bg-green-500/10 border-green-500/20">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                <AlertDescription className="text-green-700 dark:text-green-300">
                  Em homologação, as notas são emitidas normalmente mas não têm valor fiscal. 
                  Após validar tudo, mude para ambiente de <strong>Produção</strong>.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>

          <Separator />

          {/* Troubleshooting */}
          <Card className="border-dashed">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <AlertTriangle className="h-4 w-4 text-amber-500" />
                Problemas Comuns
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div className="space-y-2">
                <p className="font-medium flex items-center gap-2">
                  <Wifi className="h-4 w-4 text-red-500" />
                  Conexão recusada:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-6">
                  <li>Verifique se o ACBrMonitor está executando</li>
                  <li>Confirme que o servidor TCP está ativo na porta 3434</li>
                  <li>Verifique se o serviço FRP está rodando: <code className="bg-muted px-1 rounded">nssm status frpc</code></li>
                  <li>Teste a conexão no botão "Testar Conexão"</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <p className="font-medium flex items-center gap-2">
                  <Globe className="h-4 w-4 text-red-500" />
                  Túnel FRP não conecta:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-6">
                  <li>Verifique os logs: <code className="bg-muted px-1 rounded">C:\frp\logs\frpc-err.log</code></li>
                  <li>Confirme que a porta remota está correta no frpc.toml</li>
                  <li>Reinicie o serviço: <code className="bg-muted px-1 rounded">nssm restart frpc</code></li>
                  <li>Verifique se o firewall não está bloqueando conexões</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <p className="font-medium flex items-center gap-2">
                  <Clock className="h-4 w-4 text-red-500" />
                  Timeout de resposta:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-6">
                  <li>Aumente o timeout nas configurações</li>
                  <li>Verifique a conexão de internet do servidor</li>
                  <li>Verifique se o túnel está ativo na VPS: <code className="bg-muted px-1 rounded">ss -tlnp | grep PORTA</code></li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <p className="font-medium flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-red-500" />
                  Rejeições SEFAZ:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-6">
                  <li><strong>539 (Duplicidade):</strong> Nota já foi emitida anteriormente</li>
                  <li><strong>703 (Data/Hora):</strong> Sincronize o relógio do Windows</li>
                  <li><strong>225 (CPF inválido):</strong> Verifique formatação do CPF</li>
                  <li><strong>301/302 (IE):</strong> Verifique Inscrição Estadual</li>
                </ul>
              </div>
              
              <div className="space-y-2">
                <p className="font-medium flex items-center gap-2">
                  <Printer className="h-4 w-4 text-red-500" />
                  DANFE não imprime:
                </p>
                <ul className="list-disc list-inside text-muted-foreground space-y-1 ml-6">
                  <li>Configure a impressora na aba PosPrinter do ACBrMonitor</li>
                  <li>Porta: RAW:nome_da_impressora</li>
                  <li>Modelo: ESC-POS ou Fortes (Bobina)</li>
                  <li>Largura: 576 para impressoras 80mm</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* Custos */}
          <Card className="border-dashed bg-muted/50">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <DollarSign className="h-4 w-4 text-green-500" />
                Custos e Requisitos Legais
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-background rounded-lg">
                  <span className="font-medium block">Certificado Digital A1</span>
                  <span className="text-muted-foreground">R$ 150 - 300 / ano</span>
                </div>
                <div className="p-3 bg-background rounded-lg">
                  <span className="font-medium block">Licença ACBrMonitor PRO</span>
                  <span className="text-muted-foreground">~R$ 500 (único)</span>
                </div>
                <div className="p-3 bg-background rounded-lg">
                  <span className="font-medium block">CSC (SEFAZ)</span>
                  <span className="text-muted-foreground">Gratuito</span>
                </div>
                <div className="p-3 bg-background rounded-lg">
                  <span className="font-medium block">FRP + NSSM</span>
                  <span className="text-muted-foreground">Gratuito (open source)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
